import React, { useState, useRef, useEffect } from 'react';
import {
  Upload,
  Trash2,
  Save,
  X,
  Download,
  AlertCircle,
  CircleHelp,
  ArrowRight,
  ChevronsRight,
} from 'lucide-react';
import { toast } from 'react-toastify';
import { useLanguage } from '../contexts/LanguageContext';
import { importCsvFiles } from '../utils/exchangeImporter.js';
import { buildTemplateWorkbook } from '../utils/exchangeImportTemplates.js';
import ModalMotionShell from './ModalMotionShell';
import ModalDragHandle from './ModalDragHandle';
import {
  normalizeExchangeAccountId,
  buildExchangeRowFingerprint,
  pickExchangeAccountId,
} from '../../shared/exchangeEventId.js';

// ✅ Shared Fingerprint for Dedupe / LocalStorage / Signatures
function exchangeAccountOf(row = {}) {
  return pickExchangeAccountId(row);
}

function keyRow(r) {
  return buildExchangeRowFingerprint({
    source: r.source || r.exchange,
    exchange: r.exchange || r.source,
    exchangeAccountId: exchangeAccountOf(r),
    kind: r.kind || r.type,
    type: r.type || r.kind,
    txid: r.txid || r.txId,
    timestamp: r.timestamp || r.ts,
    side: r.side,
    base: r.base || r.baseAsset || r.asset,
    quote: r.quote || r.quoteAsset,
    amount: r.amount ?? r.baseAmount,
    quoteAmount: r.quoteAmount ?? r.quote_amount,
    price: r.price,
    fee: r.fee ?? r.feeAmount,
    feeAsset: r.feeAsset || r.fee_asset,
    importSource: r.importSource || r.import_source,
    importUid: r.importUid || r.import_uid,
  });
}

function dedupeRows(list) {
  const seen = new Set();
  const out = [];
  for (const r of list) {
    const k = keyRow(r);
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(r);
  }
  return out;
}

function buildRowsSignature(list) {
  return dedupeRows(list)
    .map(r => keyRow(r))
    .sort()
    .join('||');
}

function readLocalExchangeCache(datasetId) {
  try {
    const raw = localStorage.getItem(`eximp:${datasetId}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const rows = Array.isArray(parsed?.rows) ? dedupeRows(parsed.rows) : [];
    return {
      ...parsed,
      rows,
      signature: buildRowsSignature(rows),
    };
  } catch (e) {
        return null;
  }
}

function writeLocalExchangeCache(datasetId, rows, extra = {}) {
  const payload = {
    datasetId,
    rows: dedupeRows(rows),
    timestamp: Date.now(),
    ...extra,
  };
  localStorage.setItem(`eximp:${datasetId}`, JSON.stringify(payload));
  return payload;
}

// ✅ NEW: Kraken Export-Typ Erkennung
async function detectKrakenExportType(file) {
  try {
    const buf = await file.arrayBuffer();
    const text = new TextDecoder('utf-8').decode(buf.slice(0, 8192)).replace(/\uFEFF/g, '');
    const firstLine = text.split(/\r?\n/).find(l => l.trim().length) || '';
    const delim = firstLine.includes(';') && !firstLine.includes(',') ? ';' : ',';
    const headers = firstLine.split(delim).map(s => String(s).trim().replace(/^"+|"+$/g,'').toLowerCase());

    const has = (...keys) => keys.every(k => headers.includes(k));
    if (has('refid','balance','asset','amount')) return 'ledger';
    if (has('ordertxid','pair','price','cost')) return 'trades';
    return null;
  } catch (e) {
    return null;
  }
}

function formatIssueLocation(issue, language) {
  const parts = [];

  if (issue?.file) parts.push(issue.file);
  if (Number.isInteger(issue?.row)) {
    parts.push(language === 'en' ? `Row ${issue.row}` : `Zeile ${issue.row}`);
  }
  if (issue?.column && issue.column !== 'headers') {
    parts.push(issue.column);
  }

  return parts.join(' • ');
}

function getIssueMessage(issue, language) {
  const code = String(issue?.code || '').toUpperCase();
  const missingHeaders = Array.isArray(issue?.missingHeaders)
    ? issue.missingHeaders.join(', ')
    : '';

  switch (code) {
    case 'STANDARD_TEMPLATE_MISSING_HEADERS':
      return language === 'en'
        ? `The V3Tax standard template is missing required columns: ${missingHeaders || 'source, kind, timestamp_utc, base, amount'}.`
        : `Im V3Tax Standard-Template fehlen Pflicht-Spalten: ${missingHeaders || 'source, kind, timestamp_utc, base, amount'}.`;

    case 'STANDARD_TEMPLATE_FILE_REJECTED':
      return language === 'en'
        ? 'This V3Tax standard file was not imported because it contains validation errors.'
        : 'Diese V3Tax-Standarddatei wurde wegen Validierungsfehlern nicht importiert.';

    case 'NO_DATA_ROWS':
      return language === 'en'
        ? 'No data rows were found in this file.'
        : 'In dieser Datei wurden keine Datenzeilen gefunden.';

    case 'MISSING_KIND':
      return language === 'en'
        ? 'The field "kind" is required.'
        : 'Das Feld „kind“ ist Pflicht.';

    case 'UNSUPPORTED_KIND':
      return language === 'en'
        ? 'The value in "kind" is not supported. Allowed: trade, buy, sell, deposit, withdrawal, income.'
        : 'Der Wert in „kind“ wird nicht unterstützt. Erlaubt sind: trade, buy, sell, deposit, withdrawal, income.';

    case 'INVALID_TIMESTAMP_UTC':
    case 'INVALID_TIMESTAMP':
      return language === 'en'
        ? 'Invalid date/time. Please use UTC in the format YYYY-MM-DD HH:MM:SS.'
        : 'Ungültiges Datum/Uhrzeit. Bitte UTC im Format YYYY-MM-DD HH:MM:SS verwenden.';

    case 'MISSING_BASE':
      return language === 'en'
        ? 'The field "base" is required.'
        : 'Das Feld „base“ ist Pflicht.';

    case 'INVALID_AMOUNT':
      return language === 'en'
        ? 'The field "amount" must be a number greater than 0.'
        : 'Das Feld „amount“ muss eine Zahl größer als 0 sein.';

    case 'MISSING_SIDE':
      return language === 'en'
        ? 'Trade rows require side BUY or SELL. Alternatively use kind=buy or kind=sell.'
        : 'Trade-Zeilen benötigen side BUY oder SELL. Alternativ kind=buy oder kind=sell verwenden.';

    case 'MISSING_QUOTE':
      return language === 'en'
        ? 'Trade rows require "quote".'
        : 'Trade-Zeilen benötigen „quote“.';

    case 'MISSING_QUOTE_AMOUNT_OR_PRICE':
      return language === 'en'
        ? 'Trade rows require "quote_amount" or "price".'
        : 'Trade-Zeilen benötigen „quote_amount“ oder „price“.';

    case 'INCOME_PRICE_REQUIRES_QUOTE':
      return language === 'en'
        ? 'Income rows with "price" also require "quote".'
        : 'Income-Zeilen mit „price“ benötigen zusätzlich „quote“.';

    case 'INVALID_PRICE':
      return language === 'en'
        ? 'If "price" is filled, it must be a number greater than 0.'
        : 'Wenn „price“ ausgefüllt ist, muss es eine Zahl größer als 0 sein.';

    case 'INVALID_QUOTE_AMOUNT':
      return language === 'en'
        ? 'If "quote_amount" is filled, it must be a number greater than 0.'
        : 'Wenn „quote_amount“ ausgefüllt ist, muss es eine Zahl größer als 0 sein.';

    case 'INVALID_FEE':
      return language === 'en'
        ? 'If "fee" is filled, it must be a valid number 0 or greater.'
        : 'Wenn „fee“ ausgefüllt ist, muss es eine gültige Zahl 0 oder größer sein.';

    case 'FILE_READ_ERROR':
      return language === 'en'
        ? 'The file could not be read.'
        : 'Die Datei konnte nicht gelesen werden.';

    default:
      return issue?.message || (language === 'en' ? 'Import validation failed.' : 'Import-Validierung fehlgeschlagen.');
  }
}

function useDraggable() {
  const ref = useRef(null);
  const [pos, setPos] = useState({ x: 0, y: 0 });

  const onMouseDown = (e) => {
    if (e.button !== 0) return;

    const startX = e.clientX - pos.x;
    const startY = e.clientY - pos.y;

    const onMove = (ev) => {
      setPos({
        x: ev.clientX - startX,
        y: ev.clientY - startY,
      });
    };

    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  return { ref, pos, onMouseDown, setPos };
}

export default function ExchangeImportsManager({
  isOpen = false,
  datasetId,
  onClose,
  onOpenSupport,
  mode = "persistent",       // "persistent" | "preview"
  onConfirmPreview,          // (rows) => void
  initialRows,               // optional
  // ✅ NEU: Preview-Flow (temporär, nichts speichern)
  isPreviewFlow = false,
  onContinuePreview,
  onContinuePreviewWithoutCsv,
  isAdminFlow = false,
  adminWallets = [],
  onAdminAddAnotherWallet,
}) {
  const { language } = useLanguage();
  const [files, setFiles] = useState([]);
  const [rows, setRows] = useState([]); // unified rows
  const [isSaving, setIsSaving] = useState(false);
  const [hasLocalData, setHasLocalData] = useState(false);
  const [krakenUpload, setKrakenUpload] = useState({ trades: false, ledger: false });
  const [validationIssues, setValidationIssues] = useState([]);
  const inputRef = useRef(null);
  
  // ✅ verhindert "Server GET überschreibt lokale Uploads"
  const userTouchedRef = useRef(false);
  const rowsRef = useRef([]);
  const hasLocalDataRef = useRef(false);
  const abortRef = useRef(null);
  const initLoadKeyRef = useRef(null);
  // ✅ Ref für alle aktuell geladenen Files (für Re-Import aller Dateien zusammen)
  const filesRef = useRef([]);
  // ✅ NEU: Nur wenn true, dürfen wir aus filesRef den kompletten aktuellen Stand neu aufbauen
  const canRebuildFromFilesRef = useRef(false);
  const passwordCacheRef = useRef(new Map());

  useEffect(() => { rowsRef.current = rows; }, [rows]);
  useEffect(() => { hasLocalDataRef.current = hasLocalData; }, [hasLocalData]);

  const safeIsoFromTs = (value) => {
    const n = Number(value);
    if (!Number.isFinite(n)) return null;
    const d = new Date((n > 1e12 ? n : n * 1000));
    return Number.isFinite(d.getTime()) ? d.toISOString() : null;
  };

  const parseTsToSeconds = (value) => {
    if (typeof value === "number" && Number.isFinite(value)) {
      return value > 1e12 ? Math.floor(value / 1000) : Math.floor(value);
    }

    if (typeof value === "string" && value.trim()) {
      const ms = new Date(value).getTime();
      return Number.isFinite(ms) ? Math.floor(ms / 1000) : null;
    }

    return null;
  };
  
  const drag = useDraggable();

  // 🚀 NEU: Lade localStorage Daten beim Öffnen
  useEffect(() => {
    if (!isOpen) {
      if (abortRef.current) abortRef.current.abort();
      initLoadKeyRef.current = null;
      return;
    }

    const initKey = `${mode}:${datasetId ?? 'preview'}`;
    if (initLoadKeyRef.current === initKey) {
      return;
    }
    initLoadKeyRef.current = initKey;

    // 🧯 laufende Server-Loads abbrechen (Modal re-open / dataset switch)
    if (abortRef.current) abortRef.current.abort();
    abortRef.current = new AbortController();

    // pro Öffnen wieder "unberührt"
    userTouchedRef.current = false;

    // ✅ WICHTIG: beim Öffnen existieren evtl. Rows, aber keine echten File-Objekte mehr
    setFiles([]);
    filesRef.current = [];
    canRebuildFromFilesRef.current = false;

    if (mode === "preview") {
      setRows(Array.isArray(initialRows) ? initialRows : []);
      setHasLocalData(false);
      return;
    }

    const localCache = readLocalExchangeCache(datasetId);

    if (localCache?.rows?.length > 0) {
      setRows(localCache.rows);
      setHasLocalData(true);

      // Persistierte Rows sind nicht aus File-Objekten rekonstruierbar
      canRebuildFromFilesRef.current = false;
    }

    // Versuche auch Server-Daten zu laden
    loadServerData(abortRef.current.signal);

    return () => {
      if (abortRef.current) abortRef.current.abort();
    };
  // initialRows absichtlich nicht in den deps:
  // Ein Default "[]" oder instabile Array-Referenzen aus Parent können sonst
  // permanente Re-Loads auslösen (Request-Loop bei offenem Modal).
  }, [isOpen, datasetId, mode]);

  // 🚀 Lade Server-Daten (ohne lokalen Cache zu löschen wenn Server leer)
  const loadServerData = async (signal) => {
    if (mode === "preview") return;

    try {
      const res = await fetch(`/api/datasets/${datasetId}/exchange-imports`, { signal });
      if (!res.ok) return;

      const json = await res.json();
      if (!json.success || !Array.isArray(json.rows)) return;

      // Wenn User schon aktiv hochgeladen hat: nichts mehr drüberbügeln
      if (userTouchedRef.current) return;

      const serverRows = dedupeRows(json.rows);
      const localCache = readLocalExchangeCache(datasetId);
      const localRows = localCache?.rows || [];

      // 1) Server hat echte Daten -> übernehme sie und aktualisiere den dauerhaften Cache
      if (serverRows.length > 0) {
        setRows(serverRows);
        rowsRef.current = serverRows;
        setHasLocalData(false);

        setFiles([]);
        filesRef.current = [];
        canRebuildFromFilesRef.current = false;

        writeLocalExchangeCache(datasetId, serverRows, {
          synced: true,
          verified: true,
          source: 'server'
        });

        toast.success(
          language === 'en'
            ? `Loaded ${serverRows.length} rows from server`
            : `${serverRows.length} Zeilen vom Server geladen`
        );
        return;
      }

      // 2) Server leer, aber lokaler Cache vorhanden -> NICHT leeren
      if (localRows.length > 0) {
        setRows(localRows);
        rowsRef.current = localRows;
        setHasLocalData(true);

        setFiles([]);
        filesRef.current = [];
        canRebuildFromFilesRef.current = false;

                return;
      }

      // 3) Nur wenn wirklich beide leer sind -> leer anzeigen
      setRows([]);
      rowsRef.current = [];
      setHasLocalData(false);

      setFiles([]);
      filesRef.current = [];
      canRebuildFromFilesRef.current = false;

    } catch (error) {
      if (error?.name === 'AbortError') return;

      const localCache = readLocalExchangeCache(datasetId);
      if (localCache?.rows?.length > 0 && !userTouchedRef.current) {
        setRows(localCache.rows);
        rowsRef.current = localCache.rows;
        setHasLocalData(true);
                return;
      }

          }
  };

  const handleDownloadTemplate = async () => {
    try {
      const { buffer, fileName } = await buildTemplateWorkbook('standard', language);
      const blob = new Blob(
        [buffer],
        { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }
      );

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast.success(
        language === 'en'
          ? `Template downloaded: ${fileName}`
          : `Template heruntergeladen: ${fileName}`
      );
    } catch (error) {
            toast.error(
        language === 'en'
          ? 'Could not generate template'
          : 'Template konnte nicht erstellt werden'
      );
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    const dropped = Array.from(e.dataTransfer?.files || []).filter(f =>
      f.name.toLowerCase().endsWith('.csv') || f.name.toLowerCase().endsWith('.xlsx')
    );
    await parseFiles(dropped);
  };

  const handleFilePick = async (e) => {
    const inputEl = e.target;

    const picked = Array.from(inputEl?.files || []).filter(f =>
      f.name.toLowerCase().endsWith('.csv') || f.name.toLowerCase().endsWith('.xlsx')
    );

    // ✅ super wichtig: damit dieselbe Datei direkt erneut ausgewählt werden kann
    inputEl.value = '';

    await parseFiles(picked);
  };

  async function parseFiles(selected) {
    if (!selected.length) return;

    userTouchedRef.current = true;

    const existingRows = Array.isArray(rowsRef.current) ? rowsRef.current : [];
    const prevFiles = Array.isArray(filesRef.current) ? filesRef.current : [];

    // ✅ Nur wenn wir den aktuellen Stand vollständig aus filesRef nachbauen können,
    // dürfen wir "replace via re-import all files" machen.
    const rebuildFromFiles = canRebuildFromFilesRef.current === true;

    const nextFiles = [...prevFiles, ...selected];
    filesRef.current = nextFiles;
    setFiles(nextFiles);

    // ✅ WICHTIG:
    // - rebuildFromFiles=true  -> alle Session-Files gemeinsam neu importieren
    // - rebuildFromFiles=false -> nur neue Auswahl importieren und später MERGEN
    const filesToImport = rebuildFromFiles ? nextFiles : selected;

    try {
      // Kraken-Erkennung nur auf dem Batch, der wirklich importiert wird
      let krakenStatus = { trades: false, ledger: false };
      const kinds = await Promise.all(filesToImport.map(detectKrakenExportType));
      const nextKraken = { trades: false, ledger: false };
      for (const k of kinds) {
        if (k === 'ledger') nextKraken.ledger = true;
        if (k === 'trades') nextKraken.trades = true;
      }
      krakenStatus = nextKraken;
      setKrakenUpload(nextKraken);

      const result = await importCsvFiles(filesToImport, {
        passwordResolver: async (file) => {
          const cached =
            passwordCacheRef.current.get(file.name) ||
            passwordCacheRef.current.get('__last__');

          if (cached) return cached;

          const entered = window.prompt(
            language === 'en'
              ? `The file "${file.name}" is password-protected. Please enter the XLSX password:`
              : `Die Datei "${file.name}" ist passwortgeschützt. Bitte gib das XLSX-Passwort ein:`,
            ''
          );

          if (entered && entered.trim()) {
            passwordCacheRef.current.set(file.name, entered.trim());
            passwordCacheRef.current.set('__last__', entered.trim());
            return entered.trim();
          }

          return '';
        }
      });

      const nextValidationIssues = Array.isArray(result.validationErrors)
        ? result.validationErrors
        : [];

      setValidationIssues(nextValidationIssues);

      if (nextValidationIssues.length > 0) {
        toast.warning(
          language === 'en'
            ? `${nextValidationIssues.length} import issue(s) found. Please review the validation box above the table.`
            : `${nextValidationIssues.length} Importproblem(e) gefunden. Bitte prüfe die Validierungsbox über der Tabelle.`
        );
      }

      if (result.summary.detectedExchanges.length > 0) {
        toast.success(
          language === 'en'
            ? `Detected exchanges: ${result.summary.detectedExchanges.join(', ')}`
            : `Erkannte Börsen: ${result.summary.detectedExchanges.join(', ')}`
        );
      }

      const hasMexcTradeHistory = result.transactions.some(
        tx =>
          String(tx.exchange || '').toLowerCase() === 'mexc' &&
          String(tx.importSource || '') === 'trade_history'
      );

      const hasMexcOrderHistory = result.transactions.some(
        tx =>
          String(tx.exchange || '').toLowerCase() === 'mexc' &&
          String(tx.importSource || '') === 'order_history'
      );

      if (hasMexcOrderHistory && !hasMexcTradeHistory) {
        toast.info(
          language === 'en'
            ? 'MEXC Order History imported. Fees are not included in that export. For exact results please upload MEXC Trade History.'
            : 'MEXC Order History importiert. In diesem Export fehlen Gebühren. Für exakte Ergebnisse bitte zusätzlich die MEXC Trade History hochladen.'
        );
      }

      if (result.summary.detectedExchanges.includes('KRAKEN')) {
        const localLedger = krakenStatus.ledger;
        const localTrades = krakenStatus.trades;

        if (localTrades && !localLedger) {
          toast.info(
            language === 'en'
              ? 'Kraken Trades CSV detected. For complete data (including deposits/withdrawals), please also upload the Ledger CSV.'
              : 'Kraken Trades CSV erkannt. Für vollständige Daten (inkl. Ein-/Auszahlungen) bitte auch das Ledger CSV hochladen.'
          );
        } else if (localLedger && !localTrades) {
          toast.info(
            language === 'en'
              ? 'Kraken Ledger CSV detected. Trades will be reconstructed from ledger entries. For more accurate prices, please also upload the Trades CSV.'
              : 'Kraken Ledger CSV erkannt. Trades werden aus Ledger-Einträgen rekonstruiert. Für präzisere Preise bitte auch das Trades CSV hochladen.'
          );
        }
      }

      if (result.summary.detectedExchanges.includes('V3TAX_STANDARD')) {
        toast.info(
          language === 'en'
            ? 'V3Tax standard template detected. Rows will be imported exactly as entered.'
            : 'V3Tax Standard-Template erkannt. Die Zeilen werden exakt wie eingetragen importiert.'
        );
      }

      const convertedRows = result.transactions
        .map((tx) => {
          const base = tx.base || tx.baseAsset || tx.asset || "";
          const quote = tx.quote || tx.quoteAsset || "";
          const ts = parseTsToSeconds(tx.timestamp);
          if (!Number.isFinite(ts)) return null;

          const accountId = exchangeAccountOf(tx);
          const importUid = tx.importUid || tx.import_uid || null;
          const sourceAccountId = tx.sourceAccountId || tx.source_account_id || null;
          const targetAccountId = tx.targetAccountId || tx.target_account_id || null;

          return {
            source: String(tx.exchange || tx.source || "UNKNOWN").toUpperCase(),
            kind: tx.type || tx.kind || "",
            timestamp: ts,
            base,
            quote,
            side: tx.side ? String(tx.side).toUpperCase() : null,
            amount: tx.baseAmount ?? tx.amount ?? null,
            quoteAmount: tx.quoteAmount ?? tx.quote_amount ?? null,
            price: tx.price ?? null,
            fee: tx.feeAmount ?? tx.fee ?? null,
            feeAsset: tx.feeAsset || "",
            txid: tx.txId || tx.txid || "",
            notes: tx.note || tx.notes || "",
            exchangeAccountId: accountId,
            exchange_account_id: accountId,
            account_id: accountId,
            importUid,
            import_uid: importUid,
            sourceAccountId: sourceAccountId
              ? normalizeExchangeAccountId(sourceAccountId)
              : null,
            source_account_id: sourceAccountId
              ? normalizeExchangeAccountId(sourceAccountId)
              : null,
            targetAccountId: targetAccountId
              ? normalizeExchangeAccountId(targetAccountId)
              : null,
            target_account_id: targetAccountId
              ? normalizeExchangeAccountId(targetAccountId)
              : null,
          };
        })
        .filter(Boolean);

      if (result.hasBlockingErrors) {
        const blocking = (result.issues || result.validationErrors || []).filter(
          (x) => (x?.severity || 'error') === 'error'
        );
        const msg =
          language === 'en'
            ? `Import blocked: ${blocking.length} unresolved row(s). Existing data was kept.`
            : `Import blockiert: ${blocking.length} ungelöste Zeile(n). Bestehende Daten bleiben erhalten.`;
        toast.error(msg);
        setRows(existingRows);
        setValidationIssues(result.validationErrors || result.issues || []);
        return;
      }

      const importedRows = dedupeRows(convertedRows);

      // Wenn ein Upload nur Fehler produziert hat, bestehende Rows nicht weglöschen
      const keepExistingRows =
        importedRows.length === 0 &&
        nextValidationIssues.length > 0 &&
        existingRows.length > 0;

      const finalRows = keepExistingRows
        ? existingRows
        : (!rebuildFromFiles && existingRows.length > 0)
          ? dedupeRows([...existingRows, ...importedRows])
          : importedRows;

      setRows(finalRows);

      if (mode !== "preview") {
        setHasLocalData(true);

        const localStorageKey = `eximp:${datasetId}`;
        localStorage.setItem(localStorageKey, JSON.stringify({
          datasetId,
          rows: finalRows,
          timestamp: Date.now(),
        }));
      }

      canRebuildFromFilesRef.current =
        keepExistingRows
          ? canRebuildFromFilesRef.current
          : (rebuildFromFiles || existingRows.length === 0);

      
    } catch (error) {
            toast.error(
        language === 'en'
          ? 'Error parsing import files: ' + error.message
          : 'Fehler beim Parsen der Import-Dateien: ' + error.message
      );
    }
  }

  const handleReset = async () => {
    // ✅ lokale UI immer sofort leeren
    setRows([]);
    rowsRef.current = [];
    setFiles([]);
    filesRef.current = [];
    canRebuildFromFilesRef.current = false;
    setHasLocalData(false);
    setValidationIssues([]);
    userTouchedRef.current = true;

    // ✅ ermöglicht sofortiges Re-Upload derselben Datei
    if (inputRef.current) inputRef.current.value = '';

    // ✅ Preview-Mode: nichts dauerhaft löschen
    if (isPreviewFlow || mode === "preview") {
      // optional: auch "User touched" resetten, damit Modal sauber weiter nutzbar ist
      userTouchedRef.current = true;
      return;
    }

    // ✅ Persistent-Mode: MUSS Server + localStorage löschen, sonst kommt alles wieder
    try {
      const localStorageKey = `eximp:${datasetId}`;
      localStorage.removeItem(localStorageKey);

      userTouchedRef.current = true; // verhindert kurzzeitiges "Server GET drüberbügeln"

      const res = await fetch(`/api/datasets/${datasetId}/exchange-imports`, {
        method: 'DELETE'
      });

      const json = await res.json().catch(() => ({}));

      if (!res.ok || !json.success) {
        throw new Error(json.message || 'Delete failed');
      }

      toast.success(language === 'en'
        ? 'Exchange imports cleared'
        : 'Börsen-Importe geleert');
    } catch (e) {
      toast.error(e.message);
    }
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);

      const payload = dedupeRows(rows);
      const localStorageKey = `eximp:${datasetId}`;

      // Immer zuerst lokal als dauerhaften Cache sichern
      writeLocalExchangeCache(datasetId, payload, {
        synced: false,
        verified: false,
        source: 'local-pre-save'
      });

      if (payload.length === 0) {
        const res = await fetch(`/api/datasets/${datasetId}/exchange-imports`, {
          method: 'DELETE'
        });
        const json = await res.json().catch(() => ({}));
        if (!res.ok || !json.success) throw new Error(json.message || 'Delete failed');

        localStorage.removeItem(localStorageKey);
        setValidationIssues([]);

        toast.success(
          language === 'en'
            ? 'Exchange imports cleared'
            : 'Börsen-Importe geleert'
        );
        onClose?.();
        return;
      }

      // 1) Save
      const saveRes = await fetch(`/api/datasets/${datasetId}/exchange-imports`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rows: payload })
      });

      const saveJson = await saveRes.json().catch(() => ({}));

      if (!saveRes.ok || !saveJson.success) {
        if (Array.isArray(saveJson.errors) && saveJson.errors.length > 0) {
          setValidationIssues(saveJson.errors);
          toast.error(
            language === 'en'
              ? 'Please fix the highlighted import errors before saving.'
              : 'Bitte behebe die angezeigten Importfehler, bevor du speicherst.'
          );
          return;
        }

        throw new Error(saveJson.message || 'Upload failed');
      }

      setValidationIssues([]);

      // 2) Verify: direkt wieder laden
      const verifyRes = await fetch(`/api/datasets/${datasetId}/exchange-imports`);
      const verifyJson = await verifyRes.json().catch(() => ({}));

      const verifiedRows =
        verifyRes.ok && verifyJson.success && Array.isArray(verifyJson.rows)
          ? dedupeRows(verifyJson.rows)
          : [];

      const payloadSig = buildRowsSignature(payload);
      const verifiedSig = buildRowsSignature(verifiedRows);

      if (verifiedRows.length > 0 && verifiedSig === payloadSig) {
        // Server bestätigt exakt denselben Stand
        writeLocalExchangeCache(datasetId, verifiedRows, {
          synced: true,
          verified: true,
          source: 'server-verified'
        });

        toast.success(
          language === 'en'
            ? 'Exchange imports saved successfully'
            : 'Börsen-Importe erfolgreich gespeichert'
        );
        onClose?.();
        return;
      }

      // Save sagte ok, aber Verify passt nicht -> NICHT leeren, lokalen Cache behalten
      writeLocalExchangeCache(datasetId, payload, {
        synced: false,
        verified: false,
        source: 'verify-mismatch'
      });

      toast.warning(
        language === 'en'
          ? 'Saved locally, but server verification did not match. Data remains cached locally.'
          : 'Lokal gesichert, aber Server-Verifikation stimmt nicht. Daten bleiben lokal zwischengespeichert.'
      );

      onClose?.();

    } catch (serverError) {
      
      writeLocalExchangeCache(datasetId, dedupeRows(rows), {
        synced: false,
        verified: false,
        source: 'save-failed',
        serverError: serverError.message
      });

      toast.warning(
        language === 'en'
          ? 'Saved locally - server unavailable or verification failed'
          : 'Lokal gespeichert - Server nicht verfügbar oder Verifikation fehlgeschlagen'
      );

      onClose?.();
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    drag.setPos({ x: 0, y: 0 });
  }, [datasetId, mode, language]);

  const handleOpenSupportClick = () => {
    if (onOpenSupport) {
      onOpenSupport();
      return;
    }

    const subject = encodeURIComponent(
      language === 'en' ? 'Import Upload Problem' : 'Import-Upload Problem'
    );
    const body = encodeURIComponent(
      language === 'en'
        ? 'Hello,\n\nI am having trouble uploading my import file. Please find the file attached.\n\nThank you!'
        : 'Hallo,\n\nIch habe Probleme beim Hochladen meiner Import-Datei. Bitte finden Sie die Datei im Anhang.\n\nVielen Dank!'
    );

    window.location.href = `mailto:v3tax@proton.me?subject=${subject}&body=${body}`;
  };

  const hasRows = rows.length > 0;
  const isCompactModal = !hasRows;
  const showExpandedTransactions = hasRows;

  const tableColumns = [
    {
      key: 'source',
      label: language === 'en' ? 'Exchange' : 'Börse',
      width: '104px',
    },
    {
      key: 'kind',
      label: language === 'en' ? 'Type' : 'Art',
      width: '104px',
    },
    {
      key: 'txid',
      label: 'Tx-ID / Hash',
      width: '220px',
    },
    {
      key: 'base',
      label: language === 'en' ? 'Token' : 'Token',
      width: '92px',
    },
    {
      key: 'quote',
      label: language === 'en' ? 'Quote' : 'Quote',
      width: '92px',
    },
    {
      key: 'side',
      label: language === 'en' ? 'Side' : 'Richtung',
      width: '110px',
    },
    {
      key: 'amount',
      label: language === 'en' ? 'Amount' : 'Menge',
      width: '122px',
      align: 'right',
    },
    {
      key: 'quoteAmount',
      label: language === 'en' ? 'Quote Amount' : 'Gegenwert',
      width: '132px',
      align: 'right',
    },
    {
      key: 'price',
      label: language === 'en' ? 'Price' : 'Kurs',
      width: '118px',
      align: 'right',
    },
    {
      key: 'fee',
      label: language === 'en' ? 'Fee' : 'Gebühr',
      width: '110px',
      align: 'right',
    },
    {
      key: 'feeAsset',
      label: language === 'en' ? 'Fee Asset' : 'Fee-Asset',
      width: '112px',
    },
    {
      key: 'date',
      label: language === 'en' ? 'Date' : 'Datum',
      width: '176px',
    },
  ];

  return (
    <ModalMotionShell
      isOpen={isOpen}
      onClose={onClose}
      zIndexClass="z-[9999]"
      backdropTintClass="bg-black/55"
      backdropClassName="!backdrop-blur-[3px] px-2 py-2 sm:px-4 sm:py-4"
    >
        <div
          ref={drag.ref}
          className={`modal-container exchange-imports-modal flex ${
            isCompactModal
              ? 'h-auto max-h-[92vh] w-[min(96vw,1220px)] max-w-[1220px]'
              : 'h-[min(96vh,1080px)] w-[min(98vw,1580px)] max-w-[1580px]'
          } flex-col overflow-hidden rounded-[26px] border border-slate-200/90 bg-white shadow-[0_28px_100px_-24px_rgba(15,23,42,0.42)] ring-1 ring-slate-900/5`}
          style={{
            transform: `translate(${drag.pos.x}px, ${drag.pos.y}px)`,
            willChange: "transform",
            zIndex: 10000,
          }}
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div className="h-[4px] shrink-0 bg-gradient-to-r from-blue-700 via-blue-600 to-indigo-600" />

          {/* Header */}
          <div
            className="modal-header drag-handle shrink-0 cursor-move select-none border-b border-slate-200 bg-white/95 px-5 py-3"
            onMouseDown={drag.onMouseDown}
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex min-w-0 items-center gap-3">
                <ModalDragHandle
                  onMouseDown={drag.onMouseDown}
                  title={language === 'en' ? 'Move' : 'Verschieben'}
                  ariaLabel={language === 'en' ? 'Move modal' : 'Modal verschieben'}
                />

                <div className="min-w-0">
                  <h3 className="truncate text-[20px] font-semibold tracking-tight text-slate-900">
                    {language === 'en' ? 'Exchange Imports' : 'Börsen-Importe'}
                  </h3>
                  <p className="mt-0.5 text-sm text-slate-500">
                    {language === 'en'
                      ? 'Upload exchange CSV/XLSX exports and review all imported rows below.'
                      : 'Börsen-CSV/XLSX-Exporte hochladen und alle importierten Zeilen unten prüfen.'}
                  </p>
                </div>
              </div>

              <div className="flex shrink-0 items-center gap-2">
                <button
                  type="button"
                  className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDownloadTemplate();
                  }}
                  title={
                    language === 'en'
                      ? 'Download standard template'
                      : 'Standard-Template herunterladen'
                  }
                >
                  <Download className="h-4 w-4 text-slate-500" />
                  <span>{language === 'en' ? 'Standard-Template' : 'Standard-Template'}</span>
                </button>

                <button
                  type="button"
                  className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenSupportClick();
                  }}
                >
                  <CircleHelp className="h-4 w-4 text-amber-500" />
                  <span>{language === 'en' ? 'Report issue' : 'Problem melden'}</span>
                </button>

                <button
                  type="button"
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => {
                    e.stopPropagation();
                    onClose();
                  }}
                  className="group inline-flex h-10 w-10 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition hover:bg-slate-50 hover:text-slate-700"
                >
                  <X className="h-5 w-5 transition-transform duration-200 group-hover:rotate-90" />
                </button>
              </div>
            </div>
          </div>

          {isAdminFlow && Array.isArray(adminWallets) && adminWallets.length > 0 ? (
            <div className="shrink-0 border-b border-amber-100 bg-amber-50/80 px-6 py-3">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0">
                  <p className="text-xs font-semibold uppercase tracking-wide text-amber-800">
                    {language === 'en' ? 'Admin wallet set' : 'Admin-Wallet-Set'}
                  </p>
                  <p className="mt-1 text-sm text-amber-900/90">
                    {language === 'en'
                      ? `${adminWallets.length} wallet(s) will be included in the calculation. You can add more before continuing.`
                      : `${adminWallets.length} Wallet(s) werden in die Berechnung einbezogen. Vor dem Fortfahren können weitere hinzugefügt werden.`}
                  </p>
                  <ul className="mt-2 max-h-24 space-y-1 overflow-y-auto text-xs font-mono text-amber-950/80">
                    {adminWallets.map((w, idx) => (
                      <li key={`${w.wallet}-${idx}`} className="truncate">
                        {idx + 1}. {w.wallet}
                        {w.delegateWallet ? ` → ${w.delegateWallet}` : ''}
                      </li>
                    ))}
                  </ul>
                </div>
                {typeof onAdminAddAnotherWallet === 'function' ? (
                  <button
                    type="button"
                    onClick={onAdminAddAnotherWallet}
                    className="shrink-0 rounded-lg border border-amber-300 bg-white px-4 py-2 text-sm font-semibold text-amber-900 shadow-sm transition hover:bg-amber-50"
                  >
                    {language === 'en' ? '+ Add another wallet' : '+ Weitere Wallet hinzufügen'}
                  </button>
                ) : null}
              </div>
            </div>
          ) : null}

          {/* Main */}
          <div
            className={`flex min-h-0 ${
              showExpandedTransactions ? 'flex-1' : 'shrink-0'
            } flex-col bg-gradient-to-b from-slate-50/80 via-white to-slate-50/60 px-6 pt-4 pb-1`}
          >
            {/* Upload Zone */}
            <div
              onDragOver={(e) => e.preventDefault()}
              onDrop={handleDrop}
              className="mb-4 flex-shrink-0 rounded-2xl border border-slate-200 bg-gradient-to-b from-slate-50 to-white px-8 py-7 text-center shadow-[0_1px_2px_rgba(15,23,42,0.04)]"
            >
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl border border-blue-100 bg-blue-50 shadow-sm">
                <Upload className="h-6 w-6 text-blue-600" />
              </div>

              <div className="mx-auto max-w-[980px]">
                <h4 className="text-[18px] font-semibold tracking-tight text-slate-900">
                  {language === 'en'
                    ? 'Import exchange CSV or XLSX exports'
                    : 'CSV- oder XLSX-Exporte deiner Börse importieren'}
                </h4>

                <p className="mt-2 text-[13px] leading-5 text-slate-600 whitespace-nowrap">
                  {language === 'en'
                    ? 'Upload one or more exchange files. V3Tax detects supported exchange formats automatically and shows all recognized transactions directly below.'
                    : 'Lade eine oder mehrere Börsen-Dateien hoch. V3Tax erkennt unterstützte Börsenformate automatisch und zeigt alle erkannten Transaktionen direkt darunter an.'}
                </p>

                <p className="mt-2 text-xs leading-5 text-slate-500">
                  {language === 'en'
                    ? 'If an import fails, use “Report issue” or upload the data again with the V3Tax standard template.'
                    : 'Falls ein Import fehlschlägt, nutze „Problem melden“ oder lade die Daten mit dem V3Tax Standard-Template erneut hoch.'}
                </p>
              </div>

              <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
                <span className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-medium text-slate-600 shadow-sm">
                  CSV
                </span>
                <span className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-medium text-slate-600 shadow-sm">
                  XLSX
                </span>
                <span className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-medium text-slate-600 shadow-sm">
                  {language === 'en' ? 'Auto detection' : 'Auto-Erkennung'}
                </span>
              </div>

              <div className="mt-5 flex items-center justify-center">
                <button
                  type="button"
                  className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-6 py-3 text-sm font-medium text-white shadow-md transition-all duration-200 hover:bg-blue-700 hover:shadow-lg"
                  onClick={() => inputRef.current?.click()}
                >
                  <Upload className="h-4 w-4" />
                  {language === 'en' ? 'Select files' : 'Dateien auswählen'}
                </button>
              </div>

              <input
                ref={inputRef}
                type="file"
                multiple
                accept=".csv,.xlsx"
                className="hidden"
                onChange={handleFilePick}
              />
            </div>

            <div className="mb-3 flex shrink-0 flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
              <div className="flex flex-wrap gap-2">
                {files.length > 0 ? (
                  <>
                    {files.slice(0, 6).map((file, idx) => (
                      <span
                        key={`${file.name}-${idx}`}
                        className="inline-flex max-w-[260px] items-center rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-medium text-slate-700"
                        title={file.name}
                      >
                        <span className="truncate">{file.name}</span>
                      </span>
                    ))}
                    {files.length > 6 && (
                      <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-medium text-slate-700">
                        +{files.length - 6} {language === 'en' ? 'more' : 'weitere'}
                      </span>
                    )}
                  </>
                ) : hasLocalData && hasRows ? (
                  <span className="inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700">
                    {language === 'en'
                      ? 'Loaded saved import rows'
                      : 'Gespeicherte Import-Zeilen geladen'}
                  </span>
                ) : (
                  <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-medium text-slate-500">
                    {language === 'en'
                      ? 'No files selected yet'
                      : 'Noch keine Dateien ausgewählt'}
                  </span>
                )}
              </div>

              <div className="flex flex-wrap gap-2">
                <span className="inline-flex items-center rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700">
                  {files.length} {language === 'en' ? 'files' : 'Dateien'}
                </span>
                <span className="inline-flex items-center rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700">
                  {rows.length} {language === 'en' ? 'rows' : 'Zeilen'}
                </span>
                <span
                  className={`inline-flex items-center rounded-full px-3 py-1.5 text-xs font-semibold ${
                    validationIssues.length > 0
                      ? 'border border-amber-200 bg-amber-50 text-amber-700'
                      : 'border border-emerald-200 bg-emerald-50 text-emerald-700'
                  }`}
                >
                  {validationIssues.length > 0
                    ? `${validationIssues.length} ${language === 'en' ? 'issues' : 'Probleme'}`
                    : 'OK'}
                </span>
              </div>
            </div>

            {/* Validation */}
            {validationIssues.length > 0 && (
              <div className="mt-3 shrink-0 rounded-2xl border border-amber-300 bg-amber-50/90 p-4 shadow-sm">
                <div className="flex items-start gap-3">
                  <div className="mt-0.5 flex h-10 w-10 items-center justify-center rounded-xl border border-amber-200 bg-amber-100">
                    <AlertCircle className="h-4 w-4 text-amber-700" />
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-amber-900">
                      {language === 'en'
                        ? `${validationIssues.length} Importproblem(e) gefunden`
                        : `${validationIssues.length} Importproblem(e) gefunden`}
                    </div>

                    <div className="mt-1 text-xs text-amber-800">
                      {language === 'en'
                        ? 'Ungültige Zeilen werden übersprungen. Bitte korrigiere die Datei und lade sie erneut hoch.'
                        : 'Ungültige Zeilen werden übersprungen. Bitte korrigiere die Datei und lade sie erneut hoch.'}
                    </div>

                    <div className="mt-3 max-h-40 space-y-2 overflow-y-auto pr-1">
                      {validationIssues.slice(0, 12).map((issue, idx) => {
                        const location = formatIssueLocation(issue, language);
                        const message = getIssueMessage(issue, language);

                        return (
                          <div
                            key={`${issue.code || 'issue'}-${issue.row || idx}-${idx}`}
                            className="rounded-xl border border-amber-200 bg-white/85 px-3 py-2"
                          >
                            {location ? (
                              <div className="mb-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-amber-700">
                                {location}
                              </div>
                            ) : null}
                            <div className="text-sm leading-6 text-slate-800">{message}</div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Preview Table */}
            <div className={`flex min-h-0 flex-col px-0 pb-6 ${showExpandedTransactions ? 'flex-1' : 'shrink-0'}`}>
              <div className="flex min-h-0 flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.05)]">
                <div className="shrink-0 border-b border-slate-200 px-5 py-4">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h4 className="text-sm font-semibold text-slate-900">
                        {language === 'en' ? 'Imported transactions' : 'Importierte Transaktionen'}
                      </h4>
                      <p className="mt-1 text-xs leading-5 text-slate-500">
                        {showExpandedTransactions
                          ? (language === 'en'
                              ? 'Review recognized transactions before continuing.'
                              : 'Prüfe die erkannten Transaktionen vor dem Fortfahren.')
                          : (language === 'en'
                              ? 'The transactions area expands automatically after the first successful import.'
                              : 'Der Transaktionsbereich erweitert sich automatisch nach dem ersten erfolgreichen Import.')}
                      </p>
                    </div>

                    <div className="shrink-0">
                      <span className="inline-flex items-center rounded-full border border-blue-200 bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700">
                        {rows.length}{' '}
                        {language === 'en' ? 'rows ready' : 'Zeilen bereit'}
                      </span>
                    </div>
                  </div>
                </div>

                {showExpandedTransactions ? (
                  <div className="min-h-0 flex-1 overflow-auto">
                    <table className="min-w-full border-collapse text-[13px] text-slate-700 antialiased">
                      <colgroup>
                        {tableColumns.map((col) => (
                          <col key={col.key} style={{ width: col.width }} />
                        ))}
                      </colgroup>

                      <thead className="v3-primary-table-head v3-primary-table-head--emerald">
                        <tr>
                          {tableColumns.map((col) => (
                            <th
                              key={col.key}
                              className={`px-4 py-3 ${col.align === 'right' ? 'text-right' : 'text-left'}`}
                            >
                              <span className="v3-th-label-fix">{col.label}</span>
                            </th>
                          ))}
                        </tr>
                      </thead>

                      <tbody className="bg-white">
                        {rows.map((r, i) => {
                          const kindLower = String(r.kind || '').toLowerCase();
                          const sideKey = String(r.side || '').toUpperCase();

                          return (
                            <tr
                              key={i}
                              className="border-b border-slate-100 transition-colors hover:bg-slate-50/80"
                            >
                              <td className="px-4 py-3 align-middle">
                                <span className="inline-flex items-center rounded-full border border-blue-200 bg-blue-50 px-2.5 py-1 text-[11px] font-semibold text-blue-700">
                                  {r.source}
                                </span>
                              </td>

                              <td className="px-4 py-3 align-middle">
                                <span
                                  className={`inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-semibold ${
                                    kindLower === 'trade'
                                      ? 'border border-violet-200 bg-violet-50 text-violet-700'
                                      : kindLower === 'deposit'
                                        ? 'border border-blue-200 bg-blue-50 text-blue-700'
                                        : kindLower === 'withdrawal'
                                          ? 'border border-amber-200 bg-amber-50 text-amber-700'
                                          : 'border border-slate-200 bg-slate-100 text-slate-700'
                                  }`}
                                >
                                  {r.kind || '—'}
                                </span>
                              </td>

                              <td className="px-4 py-3 align-middle" title={r.txid || ''}>
                                <div className="max-w-[220px] truncate text-[12px] font-medium text-slate-600">
                                  {r.txid || '—'}
                                </div>
                              </td>

                              <td className="whitespace-nowrap px-4 py-3 text-slate-800">{r.base || '—'}</td>
                              <td className="whitespace-nowrap px-4 py-3 text-slate-800">{r.quote || '—'}</td>

                              <td className="px-4 py-3 align-middle">
                                {r.side ? (
                                  <span
                                    className={`inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-semibold ${
                                      sideKey === 'BUY'
                                        ? 'border border-emerald-200 bg-emerald-50 text-emerald-700'
                                        : 'border border-rose-200 bg-rose-50 text-rose-700'
                                    }`}
                                  >
                                    {sideKey === 'BUY'
                                      ? language === 'en'
                                        ? 'Buy'
                                        : 'Kauf'
                                      : language === 'en'
                                        ? 'Sell'
                                        : 'Verkauf'}
                                  </span>
                                ) : (
                                  '—'
                                )}
                              </td>

                              <td className="whitespace-nowrap px-4 py-3 text-right font-mono text-[12.5px] tabular-nums text-slate-700">
                                {r.amount ?? '—'}
                              </td>

                              <td className="whitespace-nowrap px-4 py-3 text-right font-mono text-[12.5px] tabular-nums text-slate-700">
                                {r.quoteAmount ?? r.quote_amount ?? '—'}
                              </td>

                              <td className="whitespace-nowrap px-4 py-3 text-right font-mono text-[12.5px] tabular-nums text-slate-700">
                                {r.price ?? '—'}
                              </td>

                              <td className="whitespace-nowrap px-4 py-3 text-right font-mono text-[12.5px] tabular-nums text-slate-700">
                                {r.fee ?? '—'}
                              </td>

                              <td className="whitespace-nowrap px-4 py-3 text-slate-700">{r.feeAsset || '—'}</td>

                              <td className="whitespace-nowrap px-4 py-3 text-[12.5px] text-slate-600">
                                {new Date(Number(r.timestamp) * 1000).toLocaleString(
                                  language === 'en' ? 'en-US' : 'de-DE',
                                  {
                                    year: 'numeric',
                                    month: '2-digit',
                                    day: '2-digit',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                    second: '2-digit',
                                  }
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="px-5 py-4">
                    <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50/80 px-6 py-7 text-center">
                      <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl border border-slate-200 bg-white shadow-sm">
                        <Upload className="h-5 w-5 text-slate-400" />
                      </div>

                      <div className="text-sm font-semibold text-slate-800">
                        {language === 'en'
                          ? 'No imported transactions yet'
                          : 'Noch keine importierten Transaktionen'}
                      </div>

                      <p className="mx-auto mt-1 max-w-xl text-sm text-slate-500">
                        {language === 'en'
                          ? 'After the first successful import, recognized exchange transactions will appear here automatically.'
                          : 'Nach dem ersten erfolgreichen Import erscheinen die erkannten Börsen-Transaktionen hier automatisch.'}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="shrink-0 border-t border-slate-200 bg-white px-6 py-3">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <div className="text-sm text-slate-500">
                Börsen-Importe werden empfohlen, da FIFO-Einstandswerte für Swaps und NFTs ohne diese Daten nicht korrekt berechnet werden können.
              </div>

              {isPreviewFlow || mode === 'preview' ? (
                <div className="flex flex-col gap-2 sm:flex-row">
                  <button
                    type="button"
                    className="inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold text-red-600 transition hover:bg-red-50"
                    onClick={handleReset}
                  >
                    <Trash2 size={16} />
                    Zurücksetzen
                  </button>

                  <button
                    type="button"
                    className="inline-flex items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-800 transition hover:bg-slate-50"
                    onClick={() => {
                      if (onContinuePreviewWithoutCsv) {
                        onContinuePreviewWithoutCsv({ rows, files });
                      } else if (onConfirmPreview) {
                        onConfirmPreview([]);
                      }
                    }}
                  >
                    <ChevronsRight className="h-4 w-4 text-slate-500" />
                    {language === 'en' ? 'Continue without import data' : 'Ohne Importdaten fortfahren'}
                  </button>

                  <button
                    type="button"
                    className="inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                    onClick={() => {
                      if (onContinuePreview) {
                        onContinuePreview({ rows, files });
                      } else if (onConfirmPreview) {
                        onConfirmPreview(rows);
                      }
                    }}
                    disabled={!rows || rows.length === 0}
                  >
                    <ArrowRight className="h-4 w-4" />
                    {language === 'en' ? 'Continue with import data' : 'Mit Importdaten fortfahren'}
                  </button>
                </div>
              ) : (
                <div className="flex flex-col gap-2 sm:flex-row">
                  <button
                    type="button"
                    className="inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold text-red-600 transition hover:bg-red-50"
                    onClick={handleReset}
                  >
                    <Trash2 size={16} />
                    Zurücksetzen
                  </button>

                  <button
                    type="button"
                    className="inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
                    onClick={handleSave}
                    disabled={isSaving}
                  >
                    <Save size={16} />
                    {isSaving ? 'Speichere...' : 'Speichern'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
    </ModalMotionShell>
  );
}

